import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

/**
 * Lightweight on-page tour:
 *  - Spotlight on each target (data-testid)
 *  - Numbered tooltip with title + body
 *  - Keyboard: Esc fecha, ←/→ navega
 *
 * Props:
 *  - steps: [{ selector, title, body, placement: 'bottom'|'top'|'left'|'right' }]
 *  - open, onClose
 */
export default function RecorderTour({ steps, open, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!open) return;
    const sel = steps[i]?.selector;
    if (!sel) { setRect(null); return; }
    const el = document.querySelector(sel);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    // scroll into view if needed
    if (r.top < 60 || r.bottom > window.innerHeight - 60) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [i, open, steps]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    if (!open) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    const id = setInterval(measure, 350); // catch layout shifts (guide collapse, etc.)
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") setI(v => Math.min(v + 1, steps.length - 1));
      else if (e.key === "ArrowLeft") setI(v => Math.max(v - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      window.removeEventListener("keydown", onKey);
      clearInterval(id);
    };
  }, [open, measure, steps.length, onClose]);

  if (!open) return null;
  const step = steps[i];
  const placement = step?.placement || "bottom";

  // Tooltip positioning
  let ttStyle = {};
  if (rect) {
    const margin = 14;
    const tw = 340;
    if (placement === "bottom") {
      ttStyle = { top: rect.top + rect.height + margin, left: Math.max(12, Math.min(rect.left + rect.width / 2 - tw / 2, window.innerWidth - tw - 12)), width: tw };
    } else if (placement === "top") {
      ttStyle = { top: Math.max(12, rect.top - 220), left: Math.max(12, Math.min(rect.left + rect.width / 2 - tw / 2, window.innerWidth - tw - 12)), width: tw };
    } else if (placement === "right") {
      ttStyle = { top: rect.top, left: rect.left + rect.width + margin, width: tw };
    } else if (placement === "left") {
      ttStyle = { top: rect.top, left: Math.max(12, rect.left - tw - margin), width: tw };
    }
  } else {
    // Center fallback
    ttStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 340 };
  }

  const pad = 8;
  const cutout = rect ? {
    top: Math.max(0, rect.top - pad),
    left: Math.max(0, rect.left - pad),
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  } : null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="recorder-tour-overlay">
      {/* 4 dim rectangles around the cutout (so the target stays clickable) */}
      {cutout ? (
        <>
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: cutout.top }} onClick={onClose}/>
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: cutout.top, left: 0, width: cutout.left, height: cutout.height }} onClick={onClose}/>
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: cutout.top, left: cutout.left + cutout.width, right: 0, height: cutout.height }} onClick={onClose}/>
          <div className="absolute bg-black/55 pointer-events-auto" style={{ top: cutout.top + cutout.height, left: 0, right: 0, bottom: 0 }} onClick={onClose}/>
          {/* glow ring around target */}
          <div
            className="absolute rounded-xl ring-4 ring-primary/70 shadow-[0_0_0_4px_rgba(16,185,129,0.25)] animate-pulse"
            style={{ top: cutout.top, left: cutout.left, width: cutout.width, height: cutout.height }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/55 pointer-events-auto" onClick={onClose}/>
      )}

      {/* Tooltip */}
      <div
        className="absolute pointer-events-auto rounded-xl border border-border bg-card shadow-2xl p-4"
        style={ttStyle}
        data-testid="recorder-tour-tooltip"
      >
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0 font-semibold text-sm">{i + 1}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-primary"/> {step.title}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">{step.body}</p>
          </div>
          <button onClick={onClose} className="h-6 w-6 rounded-md grid place-items-center text-muted-foreground hover:bg-muted" data-testid="recorder-tour-close">
            <X className="h-4 w-4"/>
          </button>
        </div>

        <div className="flex items-center justify-between mt-4 gap-2">
          <div className="text-[11px] text-muted-foreground">{i + 1} / {steps.length}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setI(v => Math.max(v - 1, 0))} disabled={i === 0} data-testid="recorder-tour-prev">
              <ChevronLeft className="h-3.5 w-3.5 mr-1"/> Anterior
            </Button>
            {i < steps.length - 1 ? (
              <Button size="sm" onClick={() => setI(v => Math.min(v + 1, steps.length - 1))} data-testid="recorder-tour-next">
                Próximo <ChevronRight className="h-3.5 w-3.5 ml-1"/>
              </Button>
            ) : (
              <Button size="sm" onClick={onClose} data-testid="recorder-tour-done">Concluir</Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
